import { isValidPhoneNumber } from 'react-phone-number-input';
import z from 'zod';

import type { FileDto } from '@/api/api.dto';
import type { MediaVariantDto } from '@/common/media';
import type { Location, MarinaInfo } from '@/store/marina-services/marina-service.dto.ts';

interface List<T> {
  count: number;
  items: T[];
}

export enum ServiceProviderCreatedBy {
  VESSEL = 'vessel',
  SERVICE_PROVIDER = 'service_provider',
}

export type ServiceCategory = string;

export interface ServiceProvidersFilters {
  isFavorite?: boolean;
  /** `marina` lists catalogue marinas; without it the service returns everything else. */
  type?: 'marina';
  search: string;
  vesselId: string;
  currentLat?: number;
  currentLon?: number;
  category?: ServiceCategory | null;
  howFar?: number;
}

export interface ServiceProvidersFiltersDto {
  is_favorite?: boolean;
  type?: 'marina';
  search: string;
  vessel_id: string;
  current_lat?: number;
  current_lon?: number;
  categories?: ServiceCategory[];
  how_far?: number;
}

export interface ServiceProviderImage {
  id: string;
  name: string;
  link: string;
  content_type?: string;
  variants?: Record<string, MediaVariantDto>;
}

export interface SingleServiceProvider {
  id: string;
  name: string;
  short_description: string;
  description: string;
  favorite: boolean;
  logo: FileDto | null;
  creator_id: string;
  locations: Location[];
  marina: MarinaInfo;
  note?: null | string;
  contact_info: {
    email: string;
    emergency_contact: string;
    phone: string;
    website: string;
  };
  created_by: ServiceProviderCreatedBy;
  distance?: null | number;
  images?: ServiceProviderImage[];
}

export type ServiceProvidersList = List<SingleServiceProvider>;

export const createServiceProviderSchema = z.object({
  name: z.string().min(1, 'Please enter a name.'),
  short_description: z.string().nullable(),
  description: z.string().min(1, 'Please enter a description.'),
  contact_info: z.object({
    phone: z
      .string()
      .refine((v) => v.trim() !== '' && v.trim() !== '+1', {
        error: 'Please enter a mobile number.',
      })
      .refine((v) => isValidPhoneNumber(v), {
        error: 'Please enter a valid mobile number.',
      }),
    email: z
      .string()
      .min(1, { error: 'Please enter an email address.' })
      .pipe(z.email({ error: 'Please enter a valid email address.' })),
    website: z
      .string()
      .refine(
        (val) =>
          val === '' ||
          /^(https?:\/\/)?([a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z]{2,}(\/[^\s]*)?$/i.test(val),
        { message: 'Please enter a valid website address.' },
      )
      .nullable()
      .or(z.literal('')),
  }),
  created_by: z.enum(ServiceProviderCreatedBy, 'Invalid'),
  location: z
    .object({
      name: z.string('Invalid'),
      address: z.string().nullable(),
      latitude: z.number('Invalid'),
      longitude: z.number('Invalid'),
      map_url: z.string('Invalid'),
    })
    .optional(),
  service_category: z.array(z.string()).optional(),
  certifications: z.array(z.string()).optional(),
  marine_info: z
    .object({
      max_length: z.string().nullable().optional(),
      max_beam: z.string().nullable().optional(),
      shallowest_draft: z.string().nullable().optional(),
      price_per_foot_daily: z.string().nullable().optional(),
      price_per_foot_monthly: z.string().nullable().optional(),
    })
    .optional(),
});

export type CreateUpdateServiceProvidersFormValues = z.infer<typeof createServiceProviderSchema>;

export interface PortsFiltersDto {
  current_lat?: number;
  current_lon?: number;
}

export interface PortsFilters {
  currentLat?: number;
  currentLon?: number;
}

export interface SinglePortDto {
  address: string;
  created_at: string;
  distance?: null | number;
  id: string;
  latitude: number;
  longitude: number;
  map_url: string;
  name: string;
  updated_at: string;
}

export interface SinglePort {
  address: string;
  distance?: null | number;
  id: string;
  latitude: number;
  longitude: number;
  name: string;
}

export type PortsListDto = List<SinglePortDto>;

export type PortsList = List<SinglePort>;
